import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';
import { OPERA_BARS, OPERA_REGIONS, OPERA_SILHOUETTE } from './sydney-opera.vecteezy';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;
/** The Opera House on Bennelong Point (m): in the right third (owner), clear of the hero's words, near enough
 *  the axis to stay on the narrowest stage. */
const OPERA = { x: 380, z: 920 };
/** Where the city's buildings begin along the shore (m), well clear of the Opera House (owner). */
const SHORE = { x: 700, z: 1050 };

/** A boat on the harbour, in metres: where it is, where it heads, how fast, what kind. */
interface Boat {
  x: number;
  z: number;
  /** Heading on the water (radians, 0 = away from us along z). */
  dir: number;
  speed: number;
  /** 0 a ferry, 1 a motorboat, 2 a yacht. */
  kind: 0 | 1 | 2;
  /** Out only while the counter is strong enough for its rank; fading in and out. */
  rank: number;
  shown: number;
}

/** A sulphur-crested cockatoo in the sky, in page pixels. */
interface Bird {
  x: number;
  y: number;
  base: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  bob: number;
  phase: number;
  flap: number;
  rank: number;
  shown: number;
}

/** One thing in the scene, drawn far to near. */
interface Thing {
  z: number;
  draw: () => void;
}

/**
 * Sydney's hero board (2026-10-17), after the owner's photograph: the harbour on a clear day from high
 * over Circular Quay, in true perspective (a camera 80 m up, every object placed in metres and projected).
 * A clear blue sky; the far shores' low hills and houses, the city's towers to the right; the Opera House
 * on Bennelong Point across the water, its white shells in the sun, the garden's trees beyond it; the
 * turquoise harbour; at our feet on the right the quay's promenade with its palms and figs, the road
 * curving along it. Ferries and boats cross the water drawing white wakes and cockatoos fly over, more of
 * both on the charge. The page's
 * wallpaper is the boats alone. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-sydney',
  template: '<canvas #canvas class="hsyd-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hsyd-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroSydneyComponent extends HeroMotifBase {

  /** Sky, far shores, the water. */
  private back: HTMLCanvasElement | null = null;
  /** The Opera House and the shore beyond it, between the far boats and the near ones. */
  private mid: HTMLCanvasElement | null = null;
  /** The quay at our feet. */
  private front: HTMLCanvasElement | null = null;
  private boats: Boat[] = [];
  private birds: Bird[] = [];
  private glints: [ number, number, number, number ][] = [];
  private scaleK = 1;
  // the camera: eye height (m), focal length and horizon (px), the vanishing point's x (px)
  private eye = 80;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
  }

  /** Project a point: x across (metres from the axis), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  /**
   * The quay's seawall at our feet on the right, as a line of samples (x, z, the normal into the land, its
   * length so far): it runs away from us, turns in a wide round bend (owner: rounder) and goes on level,
   * straight across, as far as the widest stage reaches (owner).
   */
  private shoreLine: { x: number; z: number; nx: number; nz: number; s: number }[] = [];

  private buildShore(): void {
    const pts: [ number, number ][] = [];
    const p0: [ number, number ] = [ -3, 100 ], p1: [ number, number ] = [ 120, 340 ];
    for ( let t = 0; t < 1; t += 0.02 ) { pts.push( [ p0[ 0 ] + ( p1[ 0 ] - p0[ 0 ] ) * t, p0[ 1 ] + ( p1[ 1 ] - p0[ 1 ] ) * t ] ); }
    // the bend: a cubic from the slant to the level, its handles long so it rounds wide
    const d = [ 123 / 270, 240 / 270 ];
    // (owner: lower, so more water shows on the right) the level run brought nearer, the bend tighter
    const c1: [ number, number ] = [ p1[ 0 ] + d[ 0 ] * 80, p1[ 1 ] + d[ 1 ] * 80 ];
    const e: [ number, number ] = [ p1[ 0 ] + 280, p1[ 1 ] + 100 ];
    const c2: [ number, number ] = [ e[ 0 ] - 170, e[ 1 ] ];
    for ( let t = 0; t <= 1; t += 0.01 ) {
      const u = 1 - t;
      pts.push( [ u * u * u * p1[ 0 ] + 3 * u * u * t * c1[ 0 ] + 3 * u * t * t * c2[ 0 ] + t * t * t * e[ 0 ], u * u * u * p1[ 1 ] + 3 * u * u * t * c1[ 1 ] + 3 * u * t * t * c2[ 1 ] + t * t * t * e[ 1 ] ] );
    }
    for ( let x = e[ 0 ] + 10; x < 12000; x += x < 1500 ? 10 : 60 ) { pts.push( [ x, e[ 1 ] ] ); }
    this.shoreLine = [];
    let len = 0;
    for ( let i = 0; i < pts.length; i++ ) {
      const a = pts[ Math.max( 0, i - 1 ) ], b = pts[ Math.min( pts.length - 1, i + 1 ) ];
      const tx = b[ 0 ] - a[ 0 ], tz = b[ 1 ] - a[ 1 ], tl = Math.hypot( tx, tz ) || 1;
      if ( i ) { len += Math.hypot( pts[ i ][ 0 ] - pts[ i - 1 ][ 0 ], pts[ i ][ 1 ] - pts[ i - 1 ][ 1 ] ); }
      this.shoreLine.push( { x: pts[ i ][ 0 ], z: pts[ i ][ 1 ], nx: tz / tl, nz: -tx / tl, s: len } );
    }
  }

  /** Whether a point lies on the quay's land side of its seawall. */
  private onQuay( x: number, z: number ): boolean {
    let best = this.shoreLine[ 0 ], bd = Infinity;
    for ( const p of this.shoreLine ) {
      const dd = ( p.x - x ) ** 2 + ( p.z - z ) ** 2;
      if ( dd < bd ) { bd = dd; best = p; }
    }
    return ( x - best.x ) * best.nx + ( z - best.z ) * best.nz > 0;
  }

  /** How far right a boat on a lane may go before the quay (the stage's edge where the water runs on). */
  private laneEnd( z: number ): number {
    let end = Infinity;
    for ( const p of this.shoreLine ) { if ( Math.abs( p.z - z ) < 6 ) { end = Math.min( end, p.x ); } }
    return end - 30;
  }

  /** Whether a point is open water (left of the quay, clear of the Opera House's point and the far shore). */
  private isWater( x: number, z: number ): boolean {
    if ( this.onQuay( x - 6, z ) ) { return false; }
    if ( Math.abs( x - OPERA.x ) < 150 && Math.abs( z - OPERA.z ) < 130 ) { return false; }
    // as in the photographs: the point is joined at its east side to the land that runs on to the right
    // (the forecourt, then the gardens), and behind its west half lies open water
    if ( z > OPERA.z - 40 && x > OPERA.x + 200 ) { return false; }
    if ( z > OPERA.z + 170 && x > OPERA.x + 60 ) { return false; }
    // the shore swinging round from the point's platform to the gardens (owner: joined, no water between)
    if ( z > OPERA.z - 40 && x > OPERA.x + 120 && x - OPERA.x > 200 - ( z - OPERA.z + 40 ) * 0.38 ) { return false; }
    return true;
  }

  /** The boats' lanes (m from us), between the Opera House's point and the quay: one boat to a lane, so
   *  none ever runs into another (owner). */
  private static readonly LANES = [ 600, 660, 720, 780 ];

  protected build(): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.F = 1.5 * H;
    this.hz = 0.365 * H;
    this.vx = W / 2;
    this.back = null;
    this.buildShore();
    const r = this.seededLocal( 17 );
    this.boats = [];
    // (owner) two lanes across the water between the Opera House and the quay, the boats only on them
    for ( let i = 0; i < HeroSydneyComponent.LANES.length; i++ ) {
      const z = HeroSydneyComponent.LANES[ i ];
      const half = ( W / 2 + 40 ) * z / this.F;
      const kind = ( i % 3 === 0 ? 0 : i % 3 === 1 ? 1 : 2 ) as 0 | 1 | 2;
      this.boats.push( {
        x: -half + r() * ( Math.min( half, this.laneEnd( z ) ) + half ), z, dir: ( r() < 0.5 ? -1 : 1 ) * Math.PI / 2,
        speed: kind === 0 ? 7 + r() * 3 : kind === 1 ? 10 + r() * 6 : 3 + r() * 2, kind,
        rank: i === 0 ? 0 : i === 2 ? 0.2 : 0.3 + r() * 0.6, shown: 0,
      } );
    }
    // the cockatoos: a few at rest, a flock on the charge (owner)
    this.birds = [];
    const k = this.scaleK;
    for ( let i = 0; i < 30; i++ ) {
      const far = r();
      this.birds.push( {
        x: r() * W, y: 0, base: H * ( 0.06 + 0.24 * r() ), dir: r() < 0.5 ? 1 : -1,
        speed: ( 50 + r() * 40 ) * k * ( 1 - 0.4 * far ), size: ( 15 - 7 * far ) * k,
        bob: ( 4 + r() * 8 ) * k, phase: r() * Math.PI * 2, flap: r() * 10,
        rank: i === 0 ? 0 : i === 1 ? 0.04 : 0.1 + r() * 0.9, shown: 0,
      } );
    }
    this.glints = [];
    for ( let i = 0; i < 140; i++ ) {
      const z = 220 + Math.pow( r(), 1.3 ) * 2400;
      const half = ( W / 2 ) * z / this.F;
      const x = -half + r() * half * 2;
      if ( this.isWater( x, z ) ) { this.glints.push( [ x, z, r() * Math.PI * 2, 0.5 + r() ] ); }
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      // the page's wallpaper: the boats alone, still, all down the page
      this.drawWallBoats( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.mid || !this.front ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    this.drawGlints( ctx );
    this.moveBoats( dt );
    // the boats beyond the point first, then the point, then the nearer boats, then the quay
    this.drawBoats( ctx, true );
    ctx.drawImage( this.mid, 0, 0, W, H );
    this.drawBoats( ctx, false );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawBirds( ctx, dt );
  }

  /**
   * The cockatoos over the harbour: quick strong beats with short glides, white against the blue; a few at
   * rest, a noisy flock on the charge, each fading in where it is called out.
   */
  private drawBirds( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const e = this.energy;
    const share = 0.08 + 0.92 * e;
    for ( const b of this.birds ) {
      const want = b.rank <= share ? 1 : 0;
      if ( dt > 0 && want && b.shown < 0.02 ) { b.x = Math.random() * W; }
      b.shown = dt > 0 ? b.shown + ( want - b.shown ) * Math.min( 1, dt * 0.6 ) : want;
      // (owner) all over the sky, each bird flying out across it and wheeling round to come back the way it
      // came: a wide loop of its own about a point of its own, smaller at the far side of the loop
      const H = this.height;
      const cx = W * ( 0.15 + 0.7 * ( ( b.base * 0.0137 + b.size * 0.31 ) % 1 ) ), cy = H * 0.18;
      const rx = W * ( 0.22 + 0.25 * ( b.speed - 30 ) / 60 ), ry = H * ( 0.04 + 0.06 * ( b.bob / ( 12 * this.scaleK ) ) );
      if ( dt > 0 ) {
        b.phase += dt * b.dir * ( b.speed / ( rx + 1 ) ) * ( 1 + 0.4 * e );
        b.flap += dt;
      }
      if ( b.shown < 0.02 ) { continue; }
      const th = b.phase + b.base;
      b.x = cx + Math.cos( th ) * rx;
      b.y = cy + Math.sin( th ) * ry + ( b.base / H - 0.18 ) * H * 0.3;
      const heading = -Math.sin( th ) * b.dir >= 0 ? 1 : -1;
      const depth = 0.75 + 0.25 * Math.sin( th );
      // a cockatoo beats more than it glides: short glides between runs of quick deep beats
      const glide = Math.min( 1, Math.max( 0, ( Math.sin( b.flap * 0.9 ) - 0.55 ) / 0.3 ) );
      const theta = b.flap * Math.PI * 2 * 2.6;
      const arm = Math.sin( theta ) * ( 1 - glide ) - 0.1 * glide;
      const hand = Math.sin( theta - 0.8 ) * ( 1 - glide ) - 0.05 * glide;
      const away = 1 - b.shown;
      ctx.save();
      ctx.globalAlpha = Math.pow( b.shown, 0.7 );
      ctx.translate( b.x, b.y - away * b.bob * 2 );
      ctx.scale( ( 1 - 0.5 * away ) * heading * depth, ( 1 - 0.5 * away ) * depth );
      this.cockatoo( ctx, b.size, arm, hand );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * One cockatoo facing right (y down), after the Istanbul gull's build: broad rounded wings in two parts
   * (the arm to the wrist at `arm`, the hand to the tip at `hand`), white, their flight feathers washed
   * pale yellow beneath; a short rounded tail; the white head with its yellow crest raised forward and a
   * dark bill.
   */
  private cockatoo( ctx: CanvasRenderingContext2D, s: number, arm: number, hand: number ): void {
    const pose = ( a: number[], t: number ) => t <= 0 ? a[ 1 ] + ( a[ 0 ] - a[ 1 ] ) * -t : a[ 1 ] + ( a[ 2 ] - a[ 1 ] ) * t;
    const bendY = pose( [ -0.4, -0.26, 0.04 ], arm );
    const tipY = pose( [ -0.74, -0.18, 0.42 ], hand ), tipX = pose( [ 0.72, 0.86, 0.78 ], hand );
    const white: RGB = [ 250, 250, 246 ], under: RGB = [ 246, 236, 176 ];
    const fill = ( pts: number[][], c: RGB, a = 1 ) => {
      ctx.fillStyle = this.rgba( c, a );
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
      ctx.closePath();
      ctx.fill();
    };
    for ( const side of [ -1, 1 ] ) {
      const sh = [ side * 0.1, -0.08 ], bk = [ side * 0.1, 0.14 ];
      const bend = [ side * 0.42, bendY ], end = [ side * tipX, tipY ], trail = [ side * 0.5, bendY + 0.28 ];
      // the wing's rounded tip: two steps of primaries near the end
      const p1 = [ side * ( tipX - 0.04 ), tipY + 0.12 ], p2 = [ side * ( tipX - 0.1 ), tipY + 0.22 ];
      fill( [ sh, bend, end, p1, p2, trail, bk ], hand > 0.2 ? this.mix( white, under, 0.5 ) : white );
      fill( [ [ bend[ 0 ] * 0.6 + end[ 0 ] * 0.4, bend[ 1 ] * 0.6 + end[ 1 ] * 0.4 + 0.08 ], p1, p2, [ trail[ 0 ] * 0.7 + end[ 0 ] * 0.3, trail[ 1 ] * 0.7 + end[ 1 ] * 0.3 ] ], under, 0.55 );
    }
    // the tail, short and rounded
    fill( [ [ -0.26, -0.05 ], [ -0.5, -0.1 ], [ -0.56, 0 ], [ -0.5, 0.1 ], [ -0.26, 0.08 ] ], [ 240, 238, 228 ] );
    ctx.fillStyle = this.rgba( white, 1 );
    ctx.beginPath(); ctx.ellipse( -0.02 * s, 0.02 * s, 0.32 * s, 0.14 * s, 0, 0, Math.PI * 2 ); ctx.fill();
    ctx.beginPath(); ctx.arc( 0.3 * s, -0.06 * s, 0.14 * s, 0, Math.PI * 2 ); ctx.fill();
    // the crest: a few yellow plumes swept up and forward
    fill( [ [ 0.24, -0.16 ], [ 0.3, -0.38 ], [ 0.36, -0.2 ], [ 0.42, -0.36 ], [ 0.42, -0.14 ] ], [ 246, 206, 60 ] );
    fill( [ [ 0.42, -0.08 ], [ 0.54, -0.04 ], [ 0.44, 0.02 ] ], [ 60, 60, 64 ] );
  }

  /** The sun's glints on the water: soft, brief, here and there. */
  private drawGlints( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    for ( const [ x, z, ph, k ] of this.glints ) {
      const a = Math.pow( Math.max( 0, Math.sin( t * 9 + ph * 5 ) ), 6 ) * 0.75;
      if ( a < 0.04 ) { continue; }
      const [ px, py ] = this.P( x, 0, z );
      const s = this.F / z * k;
      const w = Math.max( 1.2, s * 3 ), h = Math.max( 0.5, s * 0.5 );
      const g = ctx.createRadialGradient( px, py, 0, px, py, w );
      g.addColorStop( 0, `rgba(255,255,255,${ a })` );
      g.addColorStop( 1, 'rgba(255,255,255,0)' );
      ctx.save();
      ctx.translate( px, py ); ctx.scale( 1, h / w ); ctx.translate( -px, -py );
      ctx.fillStyle = g;
      ctx.fillRect( px - w, py - w, w * 2, w * 2 );
      ctx.restore();
    }
  }

  private moveBoats( dt: number ): void {
    const share = 0.35 + 0.65 * this.energy;
    for ( const b of this.boats ) {
      const want = b.rank < share ? 1 : 0;
      b.shown = dt > 0 ? b.shown + ( want - b.shown ) * Math.min( 1, dt * 1.2 ) : want;
      if ( dt <= 0 ) { continue; }
      const nx = b.x + Math.sin( b.dir ) * b.speed * dt;
      const half = ( this.width / 2 + 60 ) * b.z / this.F;
      // along its lane: past the stage's left edge or up to the quay it comes about, unseen as it fades
      if ( nx < -half - 80 || nx > Math.min( half + 80, this.laneEnd( b.z ) ) ) {
        b.dir = -b.dir;
        b.shown = 0;
        continue;
      }
      const nz = b.z;
      b.x = nx; b.z = nz;
    }
  }

  /** The boats far to near, beyond the Opera House's point or before it. */
  private drawBoats( ctx: CanvasRenderingContext2D, beyond: boolean ): void {
    const list = this.boats.filter( b => ( b.z > OPERA.z ) === beyond ).sort( ( a, b ) => b.z - a.z );
    for ( const b of list ) {
      if ( b.shown < 0.02 ) { continue; }
      this.drawBoat( ctx, b, b.shown );
    }
  }

  /**
   * One boat: its wake first, a white V of foam widening and fading behind it with the churned wash in its
   * middle; then its hull and decks. Sydney's ferries in their green and cream, motorboats white, yachts
   * under a white sail.
   */
  private drawBoat( ctx: CanvasRenderingContext2D, b: Boat, alpha: number ): void {
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    const len = b.kind === 0 ? 40 : b.kind === 1 ? 12 : 10, wid = b.kind === 0 ? 10 : b.kind === 1 ? 4 : 3.4;
    const fx = Math.sin( b.dir ), fz = Math.cos( b.dir ), sx = fz, sz = -fx;
    const at = ( u: number, v: number, y = 0 ) => P( b.x + fx * u + sx * v, y, b.z + fz * u + sz * v );
    ctx.save();
    ctx.globalAlpha = alpha;
    const wakeL = ( b.kind === 2 ? 50 : 190 ) * ( b.speed / 10 );
    for ( let q = 0; q < 30; q++ ) {
      const f = q / 30;
      const u = -len / 2 - f * wakeL;
      const spread = f * wakeL * 0.16;
      for ( const v of [ -wid / 2 - spread, wid / 2 + spread, 0 ] ) {
        const [ px, py ] = at( u, v );
        const s = this.F / Math.max( 50, b.z + fz * u );
        const rr = Math.max( 0.7, s * ( v === 0 ? wid * 0.6 : 1.8 ) * ( 0.6 + f * 1.2 ) );
        const g = ctx.createRadialGradient( px, py, 0, px, py, rr );
        g.addColorStop( 0, `rgba(255,255,255,${ ( v === 0 ? 0.6 : 0.4 ) * ( 1 - f ) })` );
        g.addColorStop( 1, 'rgba(255,255,255,0)' );
        ctx.fillStyle = g;
        ctx.save(); ctx.translate( px, py ); ctx.scale( 1, 0.4 ); ctx.translate( -px, -py );
        ctx.fillRect( px - rr, py - rr, rr * 2, rr * 2 );
        ctx.restore();
      }
    }
    const outline: [ number, number ][] = [ [ len / 2, 0 ], [ len * 0.3, wid / 2 ], [ -len / 2, wid * 0.45 ], [ -len / 2, -wid * 0.45 ], [ len * 0.3, -wid / 2 ] ];
    const ring = ( y: number, k = 1 ) => outline.map( ( [ u, v ] ) => at( u * k, v * k, y ) );
    const fill = ( pts: [ number, number ][], col: string ) => { ctx.fillStyle = col; ctx.beginPath(); pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x, y ) : ctx.moveTo( x, y ) ); ctx.closePath(); ctx.fill(); };
    // its shadow on the water, then the hull's side and its deck
    const sh = this.F / b.z;
    fill( ring( 0 ).map( ( [ x, y ] ) => [ x + sh * 1.6, y + sh * 0.7 ] as [ number, number ] ), 'rgba(0,40,50,0.22)' );
    const hullTop = b.kind === 0 ? 2.6 : 1.3;
    const hullCol = b.kind === 0 ? 'rgb(44,94,62)' : 'rgb(236,238,240)';
    const lo = ring( 0.3 ), hi = ring( hullTop );
    fill( [ lo[ 1 ], lo[ 2 ], lo[ 3 ], lo[ 4 ], hi[ 4 ], hi[ 3 ], hi[ 2 ], hi[ 1 ] ], hullCol );
    fill( hi, b.kind === 0 ? 'rgb(226,214,170)' : 'rgb(250,250,252)' );
    if ( b.kind === 0 ) {
      // the ferry's cream decks, their windows dark, the wheelhouse on top
      for ( const [ y0, y1, k ] of [ [ hullTop, 5.2, 0.82 ], [ 5.2, 7.4, 0.6 ] ] ) {
        const a = ring( y0, k ), c = ring( y1, k );
        fill( c, 'rgb(240,232,196)' );
        fill( [ a[ 1 ], a[ 2 ], c[ 2 ], c[ 1 ] ], 'rgb(232,222,182)' );
        fill( [ a[ 4 ], a[ 3 ], c[ 3 ], c[ 4 ] ], 'rgb(214,204,166)' );
        const m0 = ring( y0 + ( y1 - y0 ) * 0.4, k ), m1 = ring( y0 + ( y1 - y0 ) * 0.75, k );
        fill( [ m0[ 1 ], m0[ 2 ], m1[ 2 ], m1[ 1 ] ], 'rgba(40,50,46,0.55)' );
      }
      fill( ring( 8.6, 0.25 ), 'rgb(232,226,200)' );
    } else if ( b.kind === 2 ) {
      const m0 = at( len * 0.1, 0, 1.3 ), m1 = at( len * 0.1, 0, 13 ), boom = at( -len * 0.4, 0, 2.2 );
      ctx.fillStyle = 'rgb(252,252,250)';
      ctx.beginPath(); ctx.moveTo( m0[ 0 ], m0[ 1 ] ); ctx.lineTo( m1[ 0 ], m1[ 1 ] ); ctx.lineTo( boom[ 0 ], boom[ 1 ] ); ctx.closePath(); ctx.fill();
      ctx.fillStyle = 'rgba(150,160,170,0.35)';
      ctx.beginPath(); ctx.moveTo( m1[ 0 ], m1[ 1 ] ); ctx.lineTo( boom[ 0 ], boom[ 1 ] ); ctx.lineTo( ( m1[ 0 ] + boom[ 0 ] ) / 2 + 1, ( m1[ 1 ] + boom[ 1 ] ) / 2 ); ctx.closePath(); ctx.fill();
    } else {
      fill( ring( 2.4, 0.4 ), 'rgba(40,60,80,0.75)' );
    }
    ctx.restore();
  }

  /** The wallpaper: little boats with their wakes, in the theme's deep blue, scattered down the page. */
  private drawWallBoats( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const [ , sky, deep ] = this.five();
    const r = this.seededLocal( 5 );
    const n = Math.max( 4, Math.round( W * H / 90000 ) );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, s = ( 10 + r() * 8 ) * k, dir = r() < 0.5 ? 1 : -1;
      ctx.save();
      ctx.translate( x, y );
      ctx.scale( dir, 1 );
      ctx.globalAlpha = 0.5 + r() * 0.3;
      for ( let q = 0; q < 10; q++ ) {
        const wx = -s * ( 0.9 + q * 0.45 ), wy = s * 0.18 * ( q * 0.25 );
        ctx.fillStyle = this.rgba( sky, 0.35 * ( 1 - q / 10 ) );
        ctx.beginPath(); ctx.ellipse( wx, wy, s * 0.35, s * 0.08, 0, 0, Math.PI * 2 ); ctx.fill();
        ctx.beginPath(); ctx.ellipse( wx, -wy, s * 0.35, s * 0.08, 0, 0, Math.PI * 2 ); ctx.fill();
      }
      ctx.fillStyle = this.rgba( deep, 1 );
      ctx.beginPath();
      ctx.moveTo( s, 0 ); ctx.quadraticCurveTo( s * 0.4, s * 0.28, -s * 0.8, s * 0.2 ); ctx.lineTo( -s * 0.8, -s * 0.2 ); ctx.quadraticCurveTo( s * 0.4, -s * 0.28, s, 0 );
      ctx.fill();
      ctx.fillStyle = this.rgba( this.mix( deep, [ 255, 255, 255 ], 0.55 ), 1 );
      ctx.beginPath(); ctx.ellipse( -s * 0.1, 0, s * 0.35, s * 0.12, 0, 0, Math.PI * 2 ); ctx.fill();
      ctx.restore();
    }
  }

  /**
   * The still parts, painted once, far to near, in metres projected: the back layer (sky, the far shores
   * and the city, the water), the middle layer (the Opera House on its point and the garden's shore beyond
   * it) and the front layer (the quay at our feet: its seawall, promenade, palms and figs, the road).
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const hz = this.hz;
    const F = this.F;
    const [ ice, sky, deep, sand, ochre ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    const P = ( x: number, y: number, z: number ) => this.P( x, y, z );
    let g: CanvasRenderingContext2D;
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    const halfAt = ( z: number ) => ( W / 2 + 60 ) * z / F;
    // a clear day: the sky's deep blue paling to the horizon, the far air a little milky
    const skyTop = this.mix( deep, [ 20, 110, 200 ], 0.4 );
    const skyLow = this.mix( this.mix( ice, sky, 0.45 ), white, 0.15 );
    const air = this.mix( skyLow, sky, 0.2 );
    const inAir = ( col: RGB, z: number, strength = 0.8 ) => this.mix( col, air, ( 1 - Math.exp( -z / 4200 ) ) * strength );
    const sun: RGB = [ 255, 250, 236 ];
    const lit = ( col: RGB, k: number, z: number ) => inAir( k > 0 ? this.mix( col, sun, 0.25 * k ) : this.mix( col, [ 30, 50, 70 ], -0.3 * k ), z );
    // the harbour's turquoise, from the theme's deep blue toward green
    const teal = this.mix( deep, [ 0, 150, 140 ], 0.45 );

    // ---- the back layer ----
    const bk = canvas();
    g = bk.g;
    {
      const gr = g.createLinearGradient( 0, 0, 0, hz );
      gr.addColorStop( 0, this.rgba( skyTop, 1 ) );
      gr.addColorStop( 0.6, this.rgba( this.mix( skyTop, skyLow, 0.5 ), 1 ) );
      gr.addColorStop( 1, this.rgba( skyLow, 1 ) );
      g.fillStyle = gr;
      g.fillRect( 0, 0, W, hz + 4 );
      // the water: paler far off where it takes the sky, a deep clear turquoise near
      const wg = g.createLinearGradient( 0, hz, 0, H );
      wg.addColorStop( 0, this.rgba( this.mix( deep, air, 0.35 ), 1 ) );
      wg.addColorStop( 0.15, this.rgba( this.mix( deep, teal, 0.4 ), 1 ) );
      wg.addColorStop( 0.6, this.rgba( teal, 1 ) );
      wg.addColorStop( 1, this.rgba( this.mix( teal, [ 0, 110, 110 ], 0.35 ), 1 ) );
      g.fillStyle = wg;
      g.fillRect( 0, hz, W, H - hz );
      // ripples: soft long dabs, lighter and darker, finer far off
      const rr = this.seededLocal( 61 );
      for ( let i = 0; i < 3200; i++ ) {
        const z = 160 + Math.pow( rr(), 1.5 ) * 7000;
        const x = ( rr() - 0.5 ) * 2 * halfAt( z );
        const [ px, py ] = P( x, 0, z );
        const s = F / z;
        const w = Math.max( 0.8, s * ( 2.5 + rr() * 4 ) ), h = Math.max( 0.3, s * 0.3 );
        g.fillStyle = rr() < 0.5 ? `rgba(255,255,255,${ 0.06 + rr() * 0.07 })` : `rgba(0,50,60,${ 0.05 + rr() * 0.06 })`;
        g.beginPath(); g.ellipse( px, py, w, h, 0, 0, Math.PI * 2 ); g.fill();
      }
    }
    // the far shores: low hills of trees and houses
    for ( const [ z, hMax, seed ] of [ [ 7000, 34, 3 ], [ 4600, 24, 5 ] ] ) {
      const half = halfAt( z );
      const rr = this.seededLocal( seed );
      const hAt = ( x: number ) => hMax * ( 0.4 + 0.35 * Math.sin( x * 0.003 + seed ) + 0.25 * Math.sin( x * 0.011 + seed * 2 ) );
      const pts: [ number, number ][] = [ P( -half, 0, z ) ];
      for ( let x = -half; x <= half; x += half / 80 ) { pts.push( P( x, Math.max( 2, hAt( x ) ), z ) ); }
      pts.push( P( half, 0, z ) );
      poly( pts, this.rgba( inAir( this.mix( [ 70, 110, 80 ], [ 130, 130, 120 ], 0.35 ), z ), 1 ) );
      for ( let i = 0; i < 420; i++ ) {
        const x = ( rr() - 0.5 ) * 2 * half;
        const [ px, py ] = P( x, rr() * hAt( x ), z );
        const s = F / z;
        g.fillStyle = this.rgba( inAir( rr() < 0.4 ? [ 196, 120, 96 ] : rr() < 0.5 ? [ 244, 240, 230 ] : [ 214, 204, 184 ], z ), 0.9 );
        g.fillRect( px, py, Math.max( 0.8, s * 7 ), Math.max( 0.5, s * 3.5 ) );
      }
    }
    // a box (towers, buildings): its front and the side toward the axis, lit from the left, and its top
    const box = ( x0: number, x1: number, z0: number, d: number, y0: number, h: number, col: RGB, glass = false ) => {
      const z = z0;
      if ( x0 > 0 ) { poly( [ P( x0, y0, z0 ), P( x0, y0, z0 + d ), P( x0, h, z0 + d ), P( x0, h, z0 ) ], this.rgba( lit( col, 0.6, z ), 1 ) ); }
      if ( x1 < 0 ) { poly( [ P( x1, y0, z0 ), P( x1, y0, z0 + d ), P( x1, h, z0 + d ), P( x1, h, z0 ) ], this.rgba( lit( col, -0.6, z ), 1 ) ); }
      poly( [ P( x0, y0, z0 ), P( x1, y0, z0 ), P( x1, h, z0 ), P( x0, h, z0 ) ], this.rgba( lit( col, 0.15, z ), 1 ) );
      poly( [ P( x0, h, z0 ), P( x1, h, z0 ), P( x1, h, z0 + d ), P( x0, h, z0 + d ) ], this.rgba( lit( col, 0.4, z ), 1 ) );
      const s = F / z;
      if ( s > 0.25 ) {
        // rows of windows or glass bands
        for ( let y = y0 + 3.5; y < h - 2; y += 3.5 ) {
          const a = P( x0, y, z0 - 0.1 ), b = P( x1, y + 1.3, z0 - 0.1 );
          g.fillStyle = glass ? this.rgba( inAir( this.mix( sky, white, 0.25 ), z ), 0.4 ) : this.rgba( inAir( [ 70, 80, 90 ], z ), 0.3 );
          g.fillRect( a[ 0 ], b[ 1 ], b[ 0 ] - a[ 0 ], Math.max( 0.4, a[ 1 ] - b[ 1 ] ) );
        }
      }
    };
    this.back = bk.c;

    // ---- the middle layer: the garden's shore beyond the point, and the Opera House ----
    const md = canvas();
    g = md.g;
    // leaves, painted once per tone: the crowns of the gardens' figs and the quay's trees
    const leafCache = new Map<string, HTMLCanvasElement>();
    const leaf = ( tint: RGB ) => {
      const key = tint.map( v => Math.round( v / 6 ) ).join( ',' );
      const hit = leafCache.get( key );
      if ( hit ) { return hit; }
      const size = 32;
      const cv = document.createElement( 'canvas' );
      cv.width = cv.height = size;
      const lg = cv.getContext( '2d' )!;
      const fg = lg.createRadialGradient( size * 0.42, size * 0.4, 0, size / 2, size / 2, size * 0.5 );
      fg.addColorStop( 0, this.rgba( this.mix( tint, white, 0.14 ), 1 ) );
      fg.addColorStop( 0.9, this.rgba( tint, 1 ) );
      fg.addColorStop( 1, this.rgba( tint, 0.6 ) );
      lg.fillStyle = fg;
      lg.beginPath(); lg.ellipse( size / 2, size / 2, size * 0.48, size * 0.36, 0.5, 0, Math.PI * 2 ); lg.fill();
      leafCache.set( key, cv );
      return cv;
    };
    // a broad tree seen from above: a dark crown of clumps, its leaves lit by the sun from the upper left
    const tree = ( x: number, z: number, hgt: number, base: RGB ) => {
      const s = F / z;
      const [ bx, by ] = P( x, 0, z ), [ cx, cy ] = P( x, hgt * 0.65, z );
      const r = Math.max( 1, s * hgt * 0.42 );
      const rnd = this.seededLocal( Math.floor( x * 13 + z * 7 ) );
      const b2 = inAir( base, z ), dark = this.mix( b2, [ 10, 30, 20 ], 0.5 );
      g.strokeStyle = 'rgb(70,60,50)';
      g.lineWidth = Math.max( 0.6, s * 0.8 );
      g.beginPath(); g.moveTo( bx, by ); g.lineTo( cx, cy ); g.stroke();
      const n = Math.min( 16, 6 + Math.floor( r / 3 ) );
      const clumps: [ number, number, number ][] = [];
      for ( let q = 0; q < n; q++ ) {
        const a = rnd() * Math.PI * 2, d = Math.sqrt( rnd() ) * r * 0.6;
        clumps.push( [ cx + Math.cos( a ) * d * 1.1, cy + Math.sin( a ) * d * 0.7, r * ( 0.4 + rnd() * 0.2 ) ] );
      }
      // (owner: as Rio's trees) its shadow cast down-right first, then the crown's dark mass, then the leaves
      g.fillStyle = 'rgba(10,40,40,0.22)';
      g.beginPath(); g.ellipse( bx + r * 0.5, by + r * 0.1, r * 1.1, r * 0.4, 0, 0, Math.PI * 2 ); g.fill();
      g.fillStyle = this.rgba( dark, 1 );
      g.beginPath();
      for ( const [ qx, qy, qr ] of clumps ) { g.moveTo( qx + qr, qy ); g.arc( qx, qy, qr, 0, Math.PI * 2 ); }
      g.fill();
      if ( r < 2.5 ) {
        g.fillStyle = this.rgba( this.mix( b2, sun, 0.25 ), 1 );
        g.beginPath(); g.arc( cx - r * 0.25, cy - r * 0.25, r * 0.5, 0, Math.PI * 2 ); g.fill();
        return;
      }
      const litC = this.mix( b2, this.mix( sun, [ 220, 240, 140 ], 0.5 ), 0.35 );
      // smaller, crisper leaves and more of them, so the crown reads sharp
      const count = Math.min( 700, Math.floor( r * r * 0.9 ) );
      const ls = Math.max( 1.3, r * 0.15 );
      for ( let q = 0; q < count; q++ ) {
        const [ qx, qy, qr ] = clumps[ Math.floor( rnd() * clumps.length ) ];
        const a = rnd() * Math.PI * 2, d = Math.sqrt( rnd() ) * qr * 0.95;
        const lx = qx + Math.cos( a ) * d, ly = qy + Math.sin( a ) * d;
        const toSun = ( ( cx - lx ) + ( cy - ly ) ) / ( r * 1.4 );
        const depth = q / count;
        const tone = toSun > 0.1 ? this.mix( b2, litC, Math.min( 1, toSun ) * 0.8 ) : this.mix( b2, dark, 0.4 * ( 1 - depth ) * Math.min( 1, -toSun + 0.4 ) );
        const sz = ls * ( 0.75 + rnd() * 0.5 );
        g.drawImage( leaf( tone ), lx - sz / 2, ly - sz / 2, sz, sz );
      }
    };
    const mids: Thing[] = [];
    const addM = ( z: number, draw: () => void ) => mids.push( { z, draw } );
    // the land the point joins at its east side (owner: not an island): the forecourt's paving by the
    // building, then the gardens' lawns running on to the right and back; its seawall along the water
    addM( 3000, () => {
      // (owner: joined smoothly to the land, no water between at the back right) the gardens' shore runs
      // from behind the point's platform round its east side, a soft curve, and on along the water
      const xa = OPERA.x + 200, za = OPERA.z - 40, xc = OPERA.x + 60, zc = OPERA.z + 170, z1 = 2300;
      const half = halfAt( z1 );
      const curve: [ number, number ][] = [];
      for ( let q = 0; q <= 12; q++ ) {
        const t = q / 12, u = 1 - t;
        // from behind the platform (its back right corner) out past its east tip to the shore's front line
        curve.push( [ u * u * ( OPERA.x + 110 ) + 2 * u * t * ( OPERA.x + 190 ) + t * t * xa, u * u * zc + 2 * u * t * ( OPERA.z + 70 ) + t * t * za ] );
      }
      poly( [ ...curve.map( ( [ x, z ] ) => P( x, 3, z ) ), P( half, 3, za ), P( half, 3, z1 ), P( xc, 3, z1 ), P( xc, 3, zc ) ], this.rgba( inAir( [ 96, 136, 84 ], za ), 1 ) );
      // (owner: the ground it stands on joined to the land behind) the point's paving runs on into the
      // gardens round its back and east side, fading into the lawn in soft bands
      const apron: [ number, number, number, number ][] = [ [ xc, zc, 0, 1 ], [ OPERA.x + 110, zc, 0.5, 0.9 ], ...curve.slice( 1 ).map( ( [ x, z ] ) => [ x, z, 0.92, 0.39 ] as [ number, number, number, number ] ) ];
      const paveCol = lit( this.mix( [ 214, 196, 180 ], sand, 0.12 ), 0.3, OPERA.z );
      for ( let k = 0; k < 6; k++ ) {
        const d0 = k * 9, d1 = ( k + 1 ) * 9 + 1;
        poly( [ ...apron.map( ( [ x, z, nx, nz ] ) => P( x + nx * d0, 3.02, z + nz * d0 ) ), ...apron.slice().reverse().map( ( [ x, z, nx, nz ] ) => P( x + nx * d1, 3.02, z + nz * d1 ) ) ], this.rgba( paveCol, 1 - k / 6 ) );
      }
      // the forecourt: pale paving from the point eastward along the water
      poly( [ P( xa, 3.05, za ), P( half, 3.05, za ), P( half, 3.05, za + 40 ), P( xa - 15, 3.05, za + 40 ) ], this.rgba( lit( this.mix( [ 214, 196, 180 ], sand, 0.12 ), 0.3, za ), 1 ) );
      const wall = this.rgba( lit( this.mix( sand, [ 200, 180, 150 ], 0.5 ), 0.3, za ), 1 );
      poly( [ P( xa, 0, za ), P( half, 0, za ), P( half, 3, za ), P( xa, 3, za ) ], wall );
      for ( let q = 0; q < curve.length - 1; q++ ) {
        const [ ax, az ] = curve[ q ], [ bx, bz ] = curve[ q + 1 ];
        poly( [ P( ax, 0, az ), P( bx, 0, bz ), P( bx, 3, bz ), P( ax, 3, az ) ], wall );
      }
    } );
    {
      const rr = this.seededLocal( 31 );
      for ( let z = 2200; z > OPERA.z; z *= 0.985 ) {
        const half = halfAt( z );
        for ( let x = ( z > OPERA.z + 190 ? OPERA.x + 80 : OPERA.x + 250 ) + rr() * 20; x < half; x += 14 + rr() * 16 ) {
          const xx = x, zz = z, hh = 14 + rr() * 10, base = this.mix( [ 46, 96, 54 ], rr() < 0.5 ? [ 80, 120, 50 ] : [ 36, 84, 70 ], 0.4 );
          // the city's buildings only well off to the right (owner: apart from the Opera House)
          if ( x > SHORE.x && rr() < ( z > 1600 ? 0.22 : 0.08 ) ) { const hb = 14 + rr() * ( z > 1600 ? 40 : 22 ), bw = 18 + rr() * 16, col: RGB = rr() < 0.5 ? [ 230, 224, 210 ] : [ 206, 196, 180 ]; addM( zz, () => box( xx, xx + bw, zz, 20, 3, hb, col ) ); continue; }
          addM( zz, () => tree( xx, zz, hh, base ) );
        }
      }
    }
    // Bennelong Point and the Opera House on it, after the drawing the owner chose (sydney-opera.vecteezy.ts),
    // standing at the back of the point's platform against the land behind it, 190 m long, a little squat
    // to its real height. Its reflection on the water below it
    {
      const ox = OPERA.x, oz = OPERA.z;
      // (owner: true to the perspective, a little smaller, against the land behind) its foot 40 m back on the
      // platform, sized by that distance
      const zb = oz + 40;
      const kx = F / zb * 190 / 4008, ky = kx * 0.8;
      // (owner: the ground under it, the building standing up on it) the point's paved platform 4 m over
      // the water
      const plat = 4;
      // (owner: 20px to the left at 1440x900) the building 19 m west on its platform
      const [ gx, gy ] = P( ox - 19, plat, zb );
      // (owner: like the drawing, in the page's style) the drawing's shapes, each painted as a lit surface:
      // the shells' sunlit faces bright at the upper left and turning grey toward the lower right, their
      // narrower side faces a shade darker, the glass inside them darker up under the shell, the podium's
      // stone with its lighter ledge and the shells' soft shadow on it; no contours anywhere (owner)
      const ROLE: Record<number, string> = {};
      for ( const i of [ 3, 10, 11, 4 ] ) { ROLE[ i ] = 'bright'; }
      for ( const i of [ 18, 13, 14, 20, 19, 17, 23, 16, 15 ] ) { ROLE[ i ] = 'side'; }
      for ( const i of [ 24, 25, 29, 31, 35, 39, 45, 44, 26, 27, 28, 30, 33, 34, 37, 42, 32, 36, 38, 41, 40, 43, 46 ] ) { ROLE[ i ] = 'glass'; }
      for ( const i of [ 12, 8, 21, 5 ] ) { ROLE[ i ] = 'podium'; }
      ROLE[ 22 ] = 'ledge';
      ROLE[ 9 ] = 'platform';
      const silhouette = new Path2D( OPERA_SILHOUETTE );
      const bars = OPERA_BARS.map( d => new Path2D( d ) );
      const regions = OPERA_REGIONS.map( ( [ i, b, d ] ) => ( { i, role: ROLE[ i ], b, path: new Path2D( d ) } ) );
      const C = ( col: RGB, a2: number ) => this.rgba( inAir( col, zb, 0.3 ), a2 );
      // the drawing's foot: its own ground strip under the podium is left out (owner: the platform is ours)
      const X0 = 140, X1 = 4200, Y0 = 520, FOOT = 2735;
      const ps = kx * scale * 1.5;
      const layer = () => {
        const cv = document.createElement( 'canvas' );
        cv.width = Math.ceil( ( X1 - X0 ) * ps );
        cv.height = Math.ceil( ( FOOT - Y0 ) * ps );
        const cg = cv.getContext( '2d' )!;
        cg.scale( ps, ps );
        cg.translate( -X0, -Y0 );
        return { cv, cg };
      };
      const styleOf = ( c: CanvasRenderingContext2D, r: typeof regions[ number ] ): string | CanvasGradient => {
        const [ x0, y0, x1, y1 ] = r.b;
        if ( r.role === 'bright' ) {
          const gr = c.createLinearGradient( x0, y0, x1, y1 );
          gr.addColorStop( 0, C( [ 255, 255, 253 ], 1 ) );
          gr.addColorStop( 0.45, C( [ 250, 248, 242 ], 1 ) );
          gr.addColorStop( 1, C( [ 222, 220, 214 ], 1 ) );
          return gr;
        }
        if ( r.role === 'side' ) {
          const gr = c.createLinearGradient( x0, y0, x1, y1 );
          gr.addColorStop( 0, C( [ 240, 237, 230 ], 1 ) );
          gr.addColorStop( 1, C( [ 210, 206, 196 ], 1 ) );
          return gr;
        }
        if ( r.role === 'glass' ) {
          const gr = c.createLinearGradient( 0, y0, 0, y1 );
          gr.addColorStop( 0, C( [ 72, 104, 100 ], 1 ) );
          gr.addColorStop( 1, C( [ 104, 142, 132 ], 1 ) );
          return gr;
        }
        if ( r.role === 'podium' ) {
          const gr = c.createLinearGradient( 0, y0, 0, y1 );
          gr.addColorStop( 0, C( [ 224, 210, 192 ], 1 ) );
          gr.addColorStop( 1, C( [ 198, 184, 166 ], 1 ) );
          return gr;
        }
        return C( r.role === 'ledge' ? [ 238, 228, 212 ] : [ 226, 214, 196 ], 1 );
      };
      const art = ( () => {
        const out = layer(), o = out.cg;
        o.save();
        o.beginPath(); o.rect( X0, Y0, X1 - X0, FOOT - Y0 ); o.clip();
        // 1: the drawing's lines as soft creases: grey on the shells, a deeper stone tone down the podium
        const sg = o.createLinearGradient( 0, 2150, 0, 2350 );
        sg.addColorStop( 0, C( [ 186, 186, 182 ], 1 ) );
        sg.addColorStop( 1, C( [ 170, 156, 140 ], 1 ) );
        o.fillStyle = sg;
        o.fill( silhouette );
        // 2: the drawing's outer edge takes the colour of the shape inside it, so no contour rings the building
        const eroded = layer();
        eroded.cg.fillStyle = '#000'; eroded.cg.fill( silhouette );
        eroded.cg.globalCompositeOperation = 'destination-out';
        eroded.cg.lineWidth = 92; eroded.cg.lineJoin = 'round'; eroded.cg.stroke( silhouette );
        const band = layer();
        band.cg.fillStyle = '#000'; band.cg.fill( silhouette );
        band.cg.setTransform( 1, 0, 0, 1, 0, 0 );
        band.cg.globalCompositeOperation = 'destination-out';
        band.cg.drawImage( eroded.cv, 0, 0 );
        const edge = layer();
        edge.cg.lineJoin = 'round';
        edge.cg.lineWidth = 96;
        for ( const r of regions ) { if ( r.role !== 'glass' ) { edge.cg.strokeStyle = styleOf( edge.cg, r ); edge.cg.stroke( r.path ); } }
        edge.cg.setTransform( 1, 0, 0, 1, 0, 0 );
        edge.cg.globalCompositeOperation = 'destination-in';
        edge.cg.drawImage( band.cv, 0, 0 );
        o.save(); o.setTransform( 1, 0, 0, 1, 0, 0 ); o.drawImage( edge.cv, 0, 0 ); o.restore();
        // 3: the shapes, each grown a little so the creases between them stay fine
        o.lineJoin = 'round';
        // (owner: the lines thinner)
        o.lineWidth = 28;
        for ( const r of regions ) {
          const st = styleOf( o, r );
          o.fillStyle = st;
          o.fill( r.path );
          // (owner: every line as fine as the shells') the glass panes' mullions too
          o.strokeStyle = st;
          o.stroke( r.path );
        }
        // 4: the drawing's wide dark parts: the glass inside the shells, darker up under them; on the podium
        // the deep shadows under its steps (owner: the steps clearer) and openings
        const thick = layer(), t = thick.cg;
        const gl = t.createLinearGradient( 0, 600, 0, 2500 );
        gl.addColorStop( 0, C( [ 64, 96, 92 ], 1 ) );
        gl.addColorStop( 1, C( [ 104, 142, 132 ], 1 ) );
        t.fillStyle = gl;
        t.fill( silhouette );
        t.save();
        t.clip( silhouette );
        t.fillStyle = C( [ 124, 110, 98 ], 1 );
        t.fillRect( 0, 2300, 1262, 800 );
        t.fillStyle = C( [ 150, 138, 126 ], 1 );
        t.fillRect( 1262, 2560, 3200, 600 );
        t.restore();
        t.globalCompositeOperation = 'destination-out';
        t.lineJoin = 'round';
        t.lineWidth = 96;
        for ( const r of regions ) { if ( r.role !== 'glass' ) { t.stroke( r.path ); } t.fill( r.path ); }
        // (owner: a dark line at the top) the outline's thick corner at each shell's point is not glass
        for ( const [ x, y ] of [ [ 1512, 590 ], [ 1100, 1050 ], [ 640, 1465 ], [ 3860, 1690 ] ] ) { t.beginPath(); t.arc( x, y, 120, 0, Math.PI * 2 ); t.fill(); }
        o.save(); o.setTransform( 1, 0, 0, 1, 0, 0 ); o.drawImage( thick.cv, 0, 0 ); o.restore();
        o.fillStyle = C( [ 150, 138, 126 ], 1 );
        for ( const b of bars ) { o.fill( b ); }
        // 6 (owner: the podium under the shells, real detail): its granite cladding's panel joints, the lit
        // coping along its top, the monumental stair's treads, the colonnade of glazed openings along the
        // east podium's foot between stone piers
        const byIdx = ( n: number ) => regions.find( r => r.i === n );
        o.save();
        o.beginPath();
        for ( const r of regions ) { if ( r.role === 'podium' ) { o.rect( r.b[ 0 ], r.b[ 1 ], r.b[ 2 ] - r.b[ 0 ], r.b[ 3 ] - r.b[ 1 ] ); } }
        o.clip();
        o.strokeStyle = C( [ 168, 152, 134 ], 0.35 );
        o.lineWidth = 7;
        for ( const r of regions ) {
          if ( r.role !== 'podium' || r.i === 21 ) { continue; }
          o.save();
          o.clip( r.path );
          for ( let y = r.b[ 1 ] + 58; y < r.b[ 3 ]; y += 58 ) { o.beginPath(); o.moveTo( r.b[ 0 ], y ); o.lineTo( r.b[ 2 ], y ); o.stroke(); }
          for ( let row = 0, y = r.b[ 1 ]; y < r.b[ 3 ]; y += 58, row++ ) {
            for ( let x = r.b[ 0 ] + ( row % 2 ) * 60; x < r.b[ 2 ]; x += 120 ) { o.beginPath(); o.moveTo( x, y ); o.lineTo( x, y + 58 ); o.stroke(); }
          }
          // the coping: a lit band along the top of each block
          o.fillStyle = C( [ 242, 234, 220 ], 1 );
          o.fillRect( r.b[ 0 ], r.b[ 1 ], r.b[ 2 ] - r.b[ 0 ], 26 );
          o.restore();
        }
        const stair = byIdx( 21 );
        if ( stair ) {
          o.save();
          o.clip( stair.path );
          for ( let y = stair.b[ 1 ]; y < stair.b[ 3 ]; y += 24 ) {
            o.fillStyle = C( [ 238, 228, 212 ], 1 ); o.fillRect( stair.b[ 0 ], y, stair.b[ 2 ] - stair.b[ 0 ], 9 );
            o.fillStyle = C( [ 176, 160, 142 ], 1 ); o.fillRect( stair.b[ 0 ], y + 9, stair.b[ 2 ] - stair.b[ 0 ], 15 );
          }
          o.restore();
        }
        const east = byIdx( 5 );
        if ( east ) {
          o.save();
          o.clip( east.path );
          const wg = o.createLinearGradient( 0, 2640, 0, 2735 );
          wg.addColorStop( 0, C( [ 70, 96, 94 ], 1 ) );
          wg.addColorStop( 1, C( [ 104, 136, 128 ], 1 ) );
          for ( let x = 2620; x < 3820; x += 150 ) {
            o.fillStyle = wg;
            o.fillRect( x + 26, 2650, 98, 85 );
            // the sky's light caught in the top of each pane
            o.fillStyle = C( [ 170, 200, 200 ], 0.5 );
            o.fillRect( x + 26, 2650, 98, 12 );
          }
          o.restore();
        }
        o.restore();
        // 7 (owner: the west end's stairs in detail, as many steps as the real ones) the monumental stair
        // down the podium's west end: tread after tread, each a sunlit nosing, the fine shadow under it and
        // the riser below, lighter toward the sun at the left
        o.save();
        o.clip( silhouette );
        // (owner: the band over the stairs is not there in reality) the stair climbs right up under the shells
        o.beginPath(); o.rect( 140, 2244, 1120, FOOT - 2244 ); o.clip();
        const tread = o.createLinearGradient( 150, 0, 1260, 0 );
        tread.addColorStop( 0, C( [ 246, 240, 228 ], 1 ) );
        tread.addColorStop( 1, C( [ 228, 218, 202 ], 1 ) );
        const riser = o.createLinearGradient( 150, 0, 1260, 0 );
        riser.addColorStop( 0, C( [ 210, 196, 178 ], 1 ) );
        riser.addColorStop( 1, C( [ 188, 174, 156 ], 1 ) );
        for ( let y = 2244; y < FOOT; y += 20 ) {
          o.fillStyle = tread; o.fillRect( 140, y, 1120, 8 );
          o.fillStyle = C( [ 150, 136, 120 ], 1 ); o.fillRect( 140, y + 8, 1120, 3 );
          o.fillStyle = riser; o.fillRect( 140, y + 11, 1120, 9 );
        }
        o.restore();
        // 8: the shells' soft shadow on the podium, cast down and to the right by the sun at the upper left
        o.save();
        o.beginPath();
        for ( const r of regions ) { if ( r.role === 'podium' || r.role === 'ledge' ) { o.rect( r.b[ 0 ], r.b[ 1 ], r.b[ 2 ] - r.b[ 0 ], r.b[ 3 ] - r.b[ 1 ] ); } }
        o.clip();
        o.translate( 110, 80 );
        o.fillStyle = C( [ 120, 104, 90 ], 0.22 );
        for ( const r of regions ) { if ( r.role === 'bright' || r.role === 'side' ) { o.fill( r.path ); } }
        o.restore();
        o.restore();
        return out.cv;
      } )();
      // the painted drawing set at its place: its foot on the platform, its width the building's
      const paint = ( c: CanvasRenderingContext2D, alpha: number ) => {
        c.save();
        c.globalAlpha = alpha;
        c.drawImage( art, X0, Y0, X1 - X0, FOOT - Y0 );
        c.restore();
      };
      addM( oz + 140, () => {
        // (owner: its tip is not that long; then 30px longer to the east, 20px to the west) kept close round the building, its
        // back on the land
        const ring: [ number, number ][] = [ [ -129, 0 ], [ -72, -36 ], [ 60, -22 ], [ 179, 16 ], [ 204, 60 ], [ 120, 175 ], [ -30, 150 ], [ -119, 90 ] ];
        const pave = this.mix( [ 214, 196, 180 ], sand, 0.12 );
        // its back edge reaches the gardens' land behind it
        const Q = ( u: number, y: number, v: number ) => P( ox + u, y, oz + v );
        poly( ring.map( ( [ u, v ] ) => Q( u, plat, v ) ), this.rgba( lit( pave, 0.3, oz ), 1 ) );
        // its seawall's faces toward us, sandstone, the water's line darker at their foot
        for ( let k = 0; k < ring.length; k++ ) {
          const [ ua, va ] = ring[ k ], [ ub, vb ] = ring[ ( k + 1 ) % ring.length ];
          if ( va > 0 && vb > 0 ) { continue; }
          const wf = g.createLinearGradient( 0, Q( ua, plat, va )[ 1 ], 0, Q( ua, 0, va )[ 1 ] );
          wf.addColorStop( 0, this.rgba( lit( this.mix( pave, ochre, 0.2 ), 0.1, oz ), 1 ) );
          wf.addColorStop( 1, this.rgba( lit( this.mix( pave, [ 90, 80, 70 ], 0.4 ), -0.2, oz ), 1 ) );
          poly( [ Q( ua, 0, va ), Q( ub, 0, vb ), Q( ub, plat, vb ), Q( ua, plat, va ) ], wf );
        }
      } );
      addM( oz + 100, () => {
        // the reflection: the drawing mirrored under the waterline, faint, broken into ripple bands
        g.save();
        g.beginPath();
        const half = F / oz * 120;
        // mirrored about the water's line under it, seen only on the water before the platform
        const w0 = P( ox, 0, zb )[ 1 ], wFront = P( ox, 0, oz - 36 )[ 1 ];
        for ( let y = wFront; y < wFront + half * 0.9; y += Math.max( 1.5, half * 0.03 ) ) { g.rect( gx - half * 1.4, y, half * 2.8, Math.max( 1, half * 0.02 ) ); }
        g.clip();
        g.translate( gx, 2 * w0 - gy ); g.scale( kx, -ky * 0.9 ); g.translate( -2166, -FOOT );
        paint( g, 0.22 );
        g.restore();
        g.save();
        g.translate( gx, gy ); g.scale( kx, ky ); g.translate( -2166, -FOOT );
        paint( g, 1 );
        g.restore();
      } );
    }
    mids.sort( ( a, b ) => b.z - a.z );
    for ( const t of mids ) { t.draw(); }
    this.mid = md.c;

    // ---- the front layer: the quay at our feet ----
    const ft = canvas();
    g = ft.g;
    const fronts: Thing[] = [];
    const addF = ( z: number, draw: () => void ) => fronts.push( { z, draw } );
    const shore = this.shoreLine;
    const along = ( off: number, y: number, s0 = 0, s1 = Infinity ) => shore.filter( p => p.s >= s0 && p.s <= s1 ).map( p => P( p.x + p.nx * off, y, p.z + p.nz * off ) );
    // a place on the quay: so far along the seawall, so far in from it
    const spot = ( sAt: number, off: number ) => {
      let p = shore[ 0 ];
      for ( const q of shore ) { if ( q.s >= sAt ) { p = q; break; } }
      return { x: p.x + p.nx * off, z: p.z + p.nz * off, nx: p.nx, nz: p.nz };
    };
    const sEnd = shore[ shore.length - 1 ].s;
    addF( 5000, () => {
      // the land behind the quay, out to the stage's edge and down to our feet
      const near = 60;
      poly( [ ...along( 0, 2.4 ), P( 12000, 2.4, near ), P( -3, 2.4, near ) ], this.rgba( lit( [ 150, 150, 146 ], 0.2, 300 ), 1 ) );
      // the seawall's face over the water
      poly( [ ...along( 0, 2.4 ), ...along( 0, 0 ).reverse() ], this.rgba( lit( this.mix( sand, [ 200, 186, 160 ], 0.5 ), 0.5, 300 ), 1 ) );
      // the promenade: pale pavers along the water, its railing; a planted bed behind it
      poly( [ ...along( 0.6, 2.5 ), ...along( 9, 2.5 ).reverse() ], this.rgba( lit( [ 214, 210, 202 ], 0.3, 300 ), 1 ) );
      poly( [ ...along( 9, 2.55 ), ...along( 16, 2.55 ).reverse() ], this.rgba( lit( [ 96, 136, 70 ], 0.2, 300 ), 1 ) );
      g.strokeStyle = this.rgba( lit( [ 120, 116, 110 ], 0, 300 ), 0.7 );
      g.lineWidth = Math.max( 0.6, F / 300 * 0.5 );
      g.beginPath(); along( 0.8, 3.3 ).forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) ); g.stroke();
      // the pavers' joints, faint across the walk
      g.strokeStyle = 'rgba(120,116,110,0.12)';
      for ( let sa = 0; sa < Math.min( sEnd, 3000 ); sa += 5 ) {
        const p = spot( sa, 0 );
        const a = P( p.x + p.nx * 0.6, 2.5, p.z + p.nz * 0.6 ), b = P( p.x + p.nx * 9, 2.5, p.z + p.nz * 9 );
        g.lineWidth = Math.max( 0.3, F / p.z * 0.12 );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
      }
      // the parking bays (owner): an asphalt pocket off the road's near side, white lines at a slant
      poly( [ ...along( 24.5, 2.46 ), ...along( 34, 2.46 ).reverse() ], this.rgba( lit( [ 96, 98, 102 ], 0.1, 300 ), 1 ) );
      g.strokeStyle = 'rgba(245,245,245,0.7)';
      // (owner: the cars square in their bays) bays wide enough that both lines show, each car centred between
      // two lines, the lines as long as the cars
      const BAY = 4.2;
      // (owner: some came out as triangles, too many) one short line between each two bays, centred
      // between the cars and turned in each car's own frame, so they stay parallel round the bends
      for ( let sa = 3.4; sa < Math.min( sEnd, 5000 ) + 3.5; sa += BAY ) {
        const p = spot( sa, 29.3 ), kx2 = -p.nz, kz2 = p.nx, ang = Math.PI / 4;
        const ux = kx2 * Math.cos( ang ) - p.nx * Math.sin( ang ), uz = kz2 * Math.cos( ang ) - p.nz * Math.sin( ang );
        const a = P( p.x - ux * 2.5, 2.5, p.z - uz * 2.5 ), b = P( p.x + ux * 2.5, 2.5, p.z + uz * 2.5 );
        g.lineWidth = Math.max( 0.3, F / p.z * 0.12 );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
      }
      // the road along behind the trees: dark asphalt, its white centre line, the kerb beyond
      const roadIn = 34, roadW = 16;
      poly( [ ...along( roadIn, 2.45 ), ...along( roadIn + roadW, 2.45 ).reverse() ], this.rgba( lit( [ 86, 88, 92 ], 0.1, 300 ), 1 ) );
      g.strokeStyle = 'rgba(250,250,250,0.85)';
      for ( let sa = 0; sa < Math.min( sEnd, 3000 ); sa += 14 ) {
        const a0 = spot( sa, roadIn + roadW / 2 ), b0 = spot( sa + 7, roadIn + roadW / 2 );
        const a = P( a0.x, 2.5, a0.z ), b = P( b0.x, 2.5, b0.z );
        g.lineWidth = Math.max( 0.5, F / a0.z * 0.25 );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
      }
      poly( [ ...along( roadIn + roadW, 2.55 ), ...along( roadIn + roadW + 6, 2.55 ).reverse() ], this.rgba( lit( [ 200, 196, 188 ], 0.3, 300 ), 1 ) );
      // a lawn by the road near us
      poly( [ ...along( roadIn + roadW + 6, 2.6, 0, 240 ), ...along( 200, 2.6, 0, 240 ).reverse() ], this.rgba( lit( [ 118, 160, 84 ], 0.3, 220 ), 1 ) );
    } );
    // the cars nosed in at a slant into the bays (owner: not one behind another, not on the kerb), each a
    // small 3D car: its body, its cabin with the glass, the faces the eye can see, its shadow
    {
      const rr = this.seededLocal( 43 );
      const cols: RGB[] = [ [ 240, 240, 240 ], [ 30, 30, 34 ], [ 170, 40, 40 ], [ 120, 130, 140 ], [ 40, 60, 110 ], [ 220, 220, 214 ], [ 200, 200, 196 ] ];
      const glass: RGB = [ 40, 56, 70 ];
      const ang = Math.PI / 4;
      for ( let sa = 2.0; sa < Math.min( sEnd, 5000 ); sa += 4.2 ) {
        // (owner: the gaps made the row look shifted) every bay taken
        const p = spot( sa + 3.5, 29.3 ), col = cols[ Math.floor( rr() * cols.length ) ];
        // the car's frame: u along its length (nose in toward the walk, at the bays' slant), v across it
        const kx = -p.nz, kz = p.nx;
        const ux = kx * Math.cos( ang ) - p.nx * Math.sin( ang ), uz = kz * Math.cos( ang ) - p.nz * Math.sin( ang );
        const vx = -uz, vz = ux;
        const c = ( u: number, v: number, y: number ) => P( p.x + ux * u + vx * v, y, p.z + uz * u + vz * v );
        const sees = ( nu: number, nv: number, u: number, v: number ) => {
          const nx = ux * nu + vx * nv, nz = uz * nu + vz * nv;
          const fx = p.x + ux * u + vx * v, fz = p.z + uz * u + vz * v;
          return nx * -fx + nz * -fz > 0;
        };
        const box = ( u0: number, u1: number, v0: number, v1: number, y0: number, y1: number, top: string, lit2: string, shade: string ) => {
          const sides: [ number, number, number, number, number, number ][] = [ [ u0, v0, u1, v0, 0, -1 ], [ u1, v0, u1, v1, 1, 0 ], [ u1, v1, u0, v1, 0, 1 ], [ u0, v1, u0, v0, -1, 0 ] ];
          for ( const [ ua, va, ub, vb, nu, nv ] of sides ) {
            if ( !sees( nu, nv, ( ua + ub ) / 2, ( va + vb ) / 2 ) ) { continue; }
            poly( [ c( ua, va, y0 ), c( ub, vb, y0 ), c( ub, vb, y1 ), c( ua, va, y1 ) ], nu < 0 || nv < 0 ? lit2 : shade );
          }
          poly( [ c( u0, v0, y1 ), c( u1, v0, y1 ), c( u1, v1, y1 ), c( u0, v1, y1 ) ], top );
        };
        const base = lit( col, 0.4, p.z ), side = lit( col, -0.1, p.z ), dark = lit( col, -0.6, p.z );
        addF( p.z, () => {
          poly( [ c( -2.5, -1.1, 2.5 ), c( 2.5, -1.1, 2.5 ), c( 2.5, 1.4, 2.5 ), c( -2.5, 1.4, 2.5 ) ], 'rgba(0,0,0,0.28)' );
          box( -2.25, 2.25, -0.9, 0.9, 2.8, 3.55, this.rgba( base, 1 ), this.rgba( side, 1 ), this.rgba( dark, 1 ) );
          box( -1.0, 1.2, -0.78, 0.78, 3.55, 4.1, this.rgba( this.mix( base, glass, 0.15 ), 1 ), this.rgba( this.mix( glass, side, 0.25 ), 1 ), this.rgba( glass, 1 ) );
          poly( [ c( -0.85, -0.7, 4.12 ), c( 1.05, -0.7, 4.12 ), c( 1.05, 0.7, 4.12 ), c( -0.85, 0.7, 4.12 ) ], this.rgba( base, 1 ) );
        } );
      }
    }
    // the figs: a row in the bed before the road, two beyond it (owner: none on the road)
    {
      const rr = this.seededLocal( 47 );
      // (owner) all the way along, to the stage's edge
      for ( let sa = 0; sa < Math.min( sEnd, 6000 ); sa += 9 + rr() * 5 ) {
        for ( const off of [ 23, 62, 74 ] ) {
          const p = spot( sa + ( rr() - 0.5 ) * 3, off ), hh = off < 30 ? 8 + rr() * 3 : 11 + rr() * 6;
          const base = this.mix( [ 40, 96, 50 ], rr() < 0.5 ? [ 70, 120, 40 ] : [ 30, 80, 60 ], 0.4 );
          addF( p.z - 0.5, () => tree( p.x, p.z, hh, base ) );
        }
      }
    }
    // a palm: its trunk leaning a little, its crown of fronds from above, each a tapering green blade
    const palm = ( x: number, z: number, h: number, seed: number ) => {
      const rr = this.seededLocal( seed );
      const s = F / z;
      const [ bx, by ] = P( x, 2.5, z ), [ tx, ty ] = P( x + 1.5, 2.5 + h, z );
      g.fillStyle = 'rgba(30,40,40,0.18)';
      g.beginPath(); g.ellipse( bx + s * 3, by + s * 0.8, s * 4, s * 1.4, 0, 0, Math.PI * 2 ); g.fill();
      g.strokeStyle = 'rgb(150,130,100)';
      g.lineWidth = Math.max( 0.8, s * 0.7 );
      g.beginPath(); g.moveTo( bx, by ); g.quadraticCurveTo( bx + s * 0.3, ( by + ty ) / 2, tx, ty ); g.stroke();
      const fronds = 14;
      for ( let i = 0; i < fronds; i++ ) {
        const a = i / fronds * Math.PI * 2 + rr() * 0.2;
        const len = s * ( 4.2 + rr() * 1.2 );
        const ex = tx + Math.cos( a ) * len, ey = ty + Math.sin( a ) * len * 0.55 + len * 0.15;
        const mx = tx + Math.cos( a ) * len * 0.5, my = ty + Math.sin( a ) * len * 0.3 - len * 0.12;
        const toSun = -( Math.cos( a ) + Math.sin( a ) ) * 0.5;
        const col = this.mix( [ 70, 120, 50 ], toSun > 0 ? [ 170, 200, 90 ] : [ 30, 70, 40 ], Math.abs( toSun ) * 0.6 );
        g.fillStyle = this.rgba( col, 1 );
        const nx = -Math.sin( a ) * s * 0.5, ny = Math.cos( a ) * s * 0.3;
        g.beginPath();
        g.moveTo( tx, ty );
        g.quadraticCurveTo( mx + nx, my + ny, ex, ey );
        g.quadraticCurveTo( mx - nx, my - ny, tx, ty );
        g.fill();
      }
    };
    // the palms along the walk, all along the quay
    for ( let sa = 60; sa < Math.min( sEnd, 4000 ); sa += 34 ) {
      const h = 10 + ( ( sa * 5 ) % 5 ), p = spot( sa + ( ( sa * 7 ) % 9 ), 11 + ( ( sa * 3 ) % 3 ) );
      addF( p.z - 0.3, () => palm( p.x, p.z, h, Math.floor( sa ) ) );
    }
    // a small kiosk on the promenade near us, and the lamp posts along the road
    {
      const p = spot( 160, 5 );
      addF( p.z, () => box( p.x, p.x + 4, p.z, 4, 2.5, 6, [ 200, 206, 196 ] ) );
    }
    // (owner: by the parked cars the posts read as crashes) on the far footpath, their arms out over the road
    for ( let sa = 60; sa < Math.min( sEnd, 3000 ); sa += 60 ) {
      const p = spot( sa, 53 );
      addF( p.z - 0.2, () => {
        const a = P( p.x, 2.5, p.z ), b = P( p.x, 10, p.z ), c = P( p.x - p.nx * 3, 10, p.z - p.nz * 3 );
        g.strokeStyle = 'rgba(70,74,80,0.9)';
        g.lineWidth = Math.max( 0.5, F / p.z * 0.25 );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.lineTo( c[ 0 ], c[ 1 ] ); g.stroke();
      } );
    }
    fronts.sort( ( a, b ) => b.z - a.z );
    for ( const t of fronts ) { t.draw(); }
    this.front = ft.c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Sydney's own: pale sky, harbour blue, deep blue, sand, ochre. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 202, 228, 236 ], [ 115, 185, 208 ], [ 15, 145, 199 ], [ 236, 210, 104 ], [ 175, 132, 39 ] ];
  }
}
